<template>
  <form-layout
    v-if="project"
    :is-loading="isProjectLoading"
    :title="projectDisplayName"
    @on-dismiss="dismiss"
  >
    <div class="description">
      <created-info :created-time="project.created" :creator-sid="project.createdBy" />
    </div>

    <vs-wrapper>
      <vs-text-input :value="projectName" label="Name" required @input="setProjectName" />

      <v-row v-if="hasChanges" align="center" justify="end" no-gutters>
        <vs-button
          :loading="isProjectSaving"
          class="mr-2"
          label="Discard"
          small
          type="secondary"
          @click="discard"
        />
        <vs-button :loading="isProjectSaving" label="Save" small @click="save" />
      </v-row>

      <div class="d-flex justify-space-between align-center">
        <project-progress-bar :job-status-count="jobCountByStatus" class="flex-grow-1" />
        <project-filter
          v-model="selectedFilters"
          :disabled="selectedJobs.length > 0"
          :filter-options="filterOptions"
        />
      </div>
      <vs-wrapper v-if="!isLoading">
        <job-card
          v-for="job in projectJobs"
          :key="job.number"
          :is-selected="isJobSelected(job)"
          :job="job"
          :show-checkbox="showCheckbox"
          :link-to="{
            name: jobSideSheetUrl,
            params: {
              id: `${job.number}`,
            },
          }"
          @selected="
            (selectedJob) => {
              selectedJobs.push(selectedJob)
            }
          "
          @deselected="
            (unselectedJob) => {
              selectedJobs = selectedJobs.filter((j) => j.number != unselectedJob.number)
            }
          "
        />
      </vs-wrapper>
      <v-progress-circular v-else :size="64" :width="6" color="primary" indeterminate />
      <job-search-pagination
        :query="query"
        :current-page="getCurrentPage"
        :update-page="updatePage"
      />
    </vs-wrapper>
    <template #action-bar>
      <vs-wrapper v-if="selectedJobs.length" direction="horizontal">
        <vs-button
          v-if="bulkUpdateEnabled"
          :label="updateJobsLabel"
          full-width
          @click="isBulkUpdateFormOpen = true"
        />
        <vs-button
          v-if="showCheckbox"
          :label="selectAllButtonLabel"
          full-width
          type="secondary"
          @click="handleSelectDeselectAll"
        />
      </vs-wrapper>

      <vs-button v-else full-width label="New job" @click="openNewJobSideSheet" />
    </template>

    <template #snack-bar-wrapper>
      <vs-snackbar :value="!!snackbarMessage" sticky type="info" @input="clearSnackbarMessage"
        >{{ snackbarMessage }}
      </vs-snackbar>
      <vs-snackbar
        :value="!!hasGetProjectError || !!hasEditProjectError"
        sticky
        type="error"
        @input="clearSnackbarError"
      >
        Something went wrong, please try again
      </vs-snackbar>
    </template>

    <right-drawer :is-visible="isBulkUpdateFormOpen">
      <bulk-update-form
        :selected-jobs="selectedJobs"
        :allow-add-to-project="false"
        @dismiss="isBulkUpdateFormOpen = false"
        @updated="handleBulkUpdate"
        @bottom-sheet-closed="isBulkUpdateBottomSheetOpen = false"
        @bottom-sheet-open="isBulkUpdateBottomSheetOpen = true"
      ></bulk-update-form>
    </right-drawer>

    <template #bottom-sheet>
      <vs-bottom-sheet :is-visible="isJobFormVisible" full-height>
        <router-view @job-saved="handleJobSaved" />
      </vs-bottom-sheet>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '../common/FormLayout.vue'
import CreatedInfo from '../common/CreatedInfo.vue'
import JobCard from '@/components/jobs/job-search-page/JobCard.vue'
import ProjectProgressBar from './ProjectProgressBar.vue'
import VsWrapper from '../vision/VsWrapper.vue'
import ProjectFilter from './ProjectFilter.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
import {
  ACTIVE,
  ASSIGNED_TO_SERVICE_PROVIDER,
  CANCELLED,
  COMPLETED,
  PAUSED,
  REQUESTED,
  SCHEDULED,
} from '@/models/job'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router/composables'
import { usePagination } from '@/components/jobs/pagination'
import { useJobsQuery } from '@/components/jobs/query'
import { useJobsSearch } from '@/components/jobs/search'
import { useProjectViewFilters } from '@/components/jobs/filters'
import { useBulkUpdate } from '@/components/jobs/bulk-update'
import BulkUpdateForm from '@/components/jobs/BulkUpdateForm.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import { useJobStatuses } from '@/components/project/jobstatus'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsButton from '@/components/vision/VsButton.vue'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'
import { JobSearchResponse } from '@/models/types/job-search-response'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import { ProjectsQueryConfig, useGetProjectQuery } from '../../api/useProjectsQueries'
import { editProjectMutation } from '../../api/useProjectsMutations'
import { useQueryClient } from '@tanstack/vue-query'
import Project from '@/models/project'

const orderedStatuses = ['completed', 'active', 'unstarted']

interface Props {
  id: string
  parentRouteName: string
  parentRouteParams?: Record<string, string>
}

const props = withDefaults(defineProps<Props>(), {
  parentRouteParams: () => ({}),
})

const store = useStore()
const route = useRoute()
const router = useRouter()

const jobSideSheetUrl = computed(() =>
  route.name?.includes('ticket-info-panel') ? route.name! : `${route.name}-ticket-info-panel`,
)

// Local Data/State
const projectName = ref<string>('')
const snackbarMessage = ref<string | null>(null)
const selectedFilters = ref<{ id: string; name: string }[]>([])
const isBulkUpdateFormOpen = ref(false)
const isBulkUpdateBottomSheetOpen = ref(false)

// Paginated Fetch Jobs API
const bulkUpdateEnabled = useBulkUpdate()
const filters = useProjectViewFilters(props.id, selectedFilters)
const { getCurrentPage, updatePage } = usePagination()
const itemPerPage = ref(20)
const query = reactive(
  useJobsQuery(
    filters,
    getCurrentPage,
    itemPerPage,
    ref(['requested_start', 'number']), // sortBy
    ref([false, false]), // sortDesc
  ),
)

const queryClient = useQueryClient()

const { jobs: projectJobs, isLoading, reFetch } = useJobsSearch(query)

const {
  data: projectData,
  isLoading: isProjectLoading,
  isError: hasGetProjectError,
} = useGetProjectQuery(
  computed(() => props.id),
  {
    enabled: computed(() => !!props.id),
  },
)

const {
  mutate: updateProjectMutate,
  isLoading: isProjectSaving,
  isError: hasEditProjectError,
} = editProjectMutation()

const jobStatuses = useJobStatuses()

const project = computed(() => (projectData.value ? new Project(projectData.value) : null))

const projectDisplayName = computed(() => project.value?.displayName)

// --------------------------------------Lifecycle hooks------------------------------------------
onMounted(async () => {
  selectedFilters.value = jobStatuses.value.filter((s) => s.id !== CANCELLED && s.id !== COMPLETED)
})

watchEffect(async () => {
  if (project.value) {
    projectName.value = project.value.name
  }
})

onBeforeRouteUpdate(async (to, from, next) => {
  if (from.params.id === 'new' && !to.params.id) {
    await Promise.all([fetchJobs()])
    selectedJobs.value = projectJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status),
    )
  }
  next()
})

onBeforeUnmount(() => {
  selectedJobs.value = []
  clearError()
})

watch(Object.values(filters), () => {
  updatePage(1)
})
// ----------------------------------End Lifecycle hooks------------------------------------------

// -------------------------------------------Vuex------------------------------------------------
const jobIsSaving = computed<boolean>(() => store.state.jobs.saving)
watch(jobIsSaving, async (isSaving, wasSaving) => {
  if (wasSaving && !isSaving) {
    await Promise.all([
      fetchJobs(),
      fetchProject(props.id), // Fetch project to update the job status counter
    ])
    selectedJobs.value = projectJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status),
    )
  }
})

const selectedJobs = ref<JobSearchResponse[]>([])

// Vuex Mutations
const clearError = () => {
  store.commit('projects/CLEAR_ERROR_PROJECT')
}
// End Vuex Mutations

// -------------------------------------------End Vuex--------------------------------------------

// --------------------------------------Computed properties--------------------------------------
const filterOptions = computed(() =>
  jobStatuses.value.map((s) => ({
    ...s,
    count: jobStatusCount.value[s.id] || 0,
  })),
)

const jobStatusCount = computed(() => ({
  [REQUESTED]: project.value ? project.value.requestedJobCount : 0,
  [ASSIGNED_TO_SERVICE_PROVIDER]: project.value
    ? project.value.assignedToServiceProviderJobCount
    : 0,
  [SCHEDULED]: project.value ? project.value.scheduledJobCount : 0,
  [ACTIVE]: project.value ? project.value.activeJobCount : 0,
  [PAUSED]: project.value ? project.value.pausedJobCount : 0,
  [COMPLETED]: project.value ? project.value.completedJobCount : 0,
  [CANCELLED]: project.value ? project.value.cancelledJobCount : 0,
}))
const jobCountByStatus = computed(() => {
  const jobCountByStatus = {
    completed: project.value ? project.value.completedJobCount : 0,
    active: project.value ? project.value.activeJobCount + project.value.pausedJobCount : 0,
    unstarted: project.value
      ? project.value.requestedJobCount +
        project.value.scheduledJobCount +
        project.value.assignedToServiceProviderJobCount
      : 0,
  }

  return orderedStatuses.map((status) => ({
    count: jobCountByStatus[status] || 0,
    name: status,
  }))
})

const hasChanges = computed(() => projectName.value !== project.value?.name)

const isJobFormVisible = computed(() => route.name?.includes('ticket-info-panel') ?? false)

const updateJobsLabel = computed(() => {
  if (selectedJobs.value.length === 1) return 'Update job'
  return `Update ${selectedJobs.value.length} jobs`
})

const showCheckbox = computed(() => selectedJobs.value.length > 0)

const areAllJobsSelected = computed(() => {
  const selectedJobIds = selectedJobs.value.map((j) => j.number)
  for (const job of projectJobs.value) {
    if (!selectedJobIds.includes(job.number)) {
      return false
    }
  }
  return true
})
const selectAllButtonLabel = computed(() =>
  areAllJobsSelected.value ? 'Deselect all' : 'Select all',
)
// ----------------------------------End Computed properties--------------------------------------

// ----------------------------------------Methods------------------------------------------------

const fetchProject = (id: string) => {
  queryClient.invalidateQueries([ProjectsQueryConfig.queryKey, 'details', id])
}

const updateProject = (obj: { projectId: string; projectName: string }) => {
  updateProjectMutate({ id: obj.projectId, name: obj.projectName })
}

async function fetchJobs() {
  await reFetch()
}

function setProjectName(newName: string) {
  projectName.value = newName
}

async function save() {
  updateProject({
    projectId: props.id,
    projectName: projectName.value,
  })

  if (hasEditProjectError) return

  snackbarMessage.value = 'Project saved'
}

function discard() {
  projectName.value = project.value?.name ?? ''
}

function dismiss() {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

function clearSnackbarMessage() {
  snackbarMessage.value = null
}

function clearSnackbarError() {
  clearError()
}

function openNewJobSideSheet() {
  if (!project.value) {
    return
  }

  router.push({
    name: `${route.name}-ticket-info-panel`,
    params: {
      id: 'new',
      hubId: project.value.ownerId,
    },
  })
}

function isJobSelected(job: JobSearchResponse) {
  return selectedJobs.value.map((job) => job.number).includes(job.number)
}

function handleSelectDeselectAll() {
  if (areAllJobsSelected.value) {
    selectedJobs.value = []
  } else {
    projectJobs.value.forEach((job) => {
      if (!selectedJobs.value.map((j) => j.number).includes(job.number)) {
        selectedJobs.value.push(job)
      }
    })
  }
}

async function handleBulkUpdate(updatedJobs: JobSearchResponse[]) {
  await Promise.all([
    fetchJobs(),
    fetchProject(props.id), // Fetch project to update the job status counter
  ])
  selectedJobs.value = updatedJobs.filter((job) =>
    selectedFilters.value.map((f) => f.id).includes(job.status),
  )
}

async function handleJobSaved(newJob: { jobNumber: number | null }) {
  await Promise.all([
    fetchJobs(),
    fetchProject(props.id), // Fetch project to update the job status counter
  ])
  selectedJobs.value = projectJobs.value.filter(
    (job) =>
      selectedJobs.value.map((j) => j.number).includes(job.number) &&
      job.number !== newJob.jobNumber &&
      selectedFilters.value.map((f) => f.id).includes(job.status),
  )
}

// -------------------------------------End Methods------------------------------------------------
</script>

<style scoped>
.description {
  margin-top: calc(-1 * var(--space-base));
  margin-bottom: var(--space-small);
}
</style>
